/**
 * Message composer component with emoji support, @mentions, and image attachments.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Textarea,
  IconButton,
  Button,
  Tooltip,
  Chip,
  CircularProgress,
} from '@mui/joy';
import {
  PaperPlaneTilt as SendIcon,
  Paperclip as AttachFileIcon,
  Smiley as EmojiEmotionsIcon,
  X as CloseIcon,
} from '@phosphor-icons/react';
import type { MessageCreatePayload, MessageAttachment } from '../../types/chat';
import EmojiPicker from './EmojiPicker';
import MentionAutocomplete from './MentionAutocomplete';
import { useUploadChatAttachment } from '../../hooks/useChat';

// Allowed image types for chat attachments
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

interface PendingAttachment extends MessageAttachment {
  id: string; // Unique identifier for removal
  previewUrl?: string; // Local preview URL
  isUploading?: boolean;
}

interface MentionedUser {
  id: number;
  name: string;
}

interface MessageComposerProps {
  channelId: string;
  placeholder?: string;
  disabled?: boolean;
  replyToMessageId?: string | null;
  currentUserId?: string | number;
  onSend: (data: MessageCreatePayload) => void;
  onTyping?: () => void;
  onCancelReply?: () => void;
}

const MessageComposer: React.FC<MessageComposerProps> = ({
  channelId,
  placeholder = 'Type a message...',
  disabled = false,
  replyToMessageId,
  currentUserId,
  onSend,
  onTyping,
  onCancelReply,
}) => {
  const [content, setContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [mentionedUsers, setMentionedUsers] = useState<MentionedUser[]>([]);
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);

  // Mention autocomplete state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionPosition, setMentionPosition] = useState({ top: 0, left: 0 });
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const uploadMutation = useUploadChatAttachment();

  // Reset content when channel changes
  useEffect(() => {
    setContent('');
    setMentionedUsers([]);
    setShowMentions(false);
    // Clean up pending attachment preview URLs
    pendingAttachments.forEach((att) => {
      if (att.previewUrl) {
        URL.revokeObjectURL(att.previewUrl);
      }
    });
    setPendingAttachments([]);
  }, [channelId]);

  // Handle typing indicator
  const handleTyping = useCallback(() => {
    if (onTyping) {
      onTyping();
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      // Typing stopped is handled by the hook
    }, 2000);
  }, [onTyping]);

  // Detect @ mentions while typing
  const detectMention = useCallback((text: string, cursorPosition: number) => {
    // Find the last @ before cursor that isn't part of an email
    let atIndex = -1;
    for (let i = cursorPosition - 1; i >= 0; i--) {
      const char = text[i];
      if (char === '@') {
        // Check if it's at start or preceded by whitespace (not part of email)
        if (i === 0 || /\s/.test(text[i - 1])) {
          atIndex = i;
          break;
        }
      } else if (/\s/.test(char)) {
        break;
      }
    }

    if (atIndex >= 0) {
      const query = text.slice(atIndex + 1, cursorPosition);
      // Only show if query doesn't contain spaces (single word)
      if (!/\s/.test(query)) {
        setMentionQuery(query);
        setMentionStartIndex(atIndex);

        // Calculate position for the autocomplete
        if (composerRef.current) {
          const rect = composerRef.current.getBoundingClientRect();
          setMentionPosition({
            top: rect.top - 10, // Position above the composer
            left: rect.left + 50,
          });
        }

        setShowMentions(true);
        return;
      }
    }

    setShowMentions(false);
    setMentionStartIndex(-1);
  }, []);

  // Handle file upload
  const handleFileUpload = useCallback(async (file: File) => {
    // Validate file type
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      alert('Only images are allowed (JPEG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      alert('File too large. Maximum size is 10MB');
      return;
    }

    // Create pending attachment with local preview
    const id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const previewUrl = URL.createObjectURL(file);

    const pendingAttachment: PendingAttachment = {
      id,
      url: '',
      name: file.name,
      type: file.type,
      size: file.size,
      previewUrl,
      isUploading: true,
    };

    setPendingAttachments((prev) => [...prev, pendingAttachment]);

    try {
      // Upload to server
      const result = await uploadMutation.mutateAsync(file);

      // Update pending attachment with server URL
      setPendingAttachments((prev) =>
        prev.map((att) =>
          att.id === id
            ? { ...att, url: result.url, isUploading: false }
            : att
        )
      );
    } catch (error) {
      // Remove failed attachment
      setPendingAttachments((prev) => prev.filter((att) => att.id !== id));
      URL.revokeObjectURL(previewUrl);
      alert('Failed to upload image. Please try again.');
    }
  }, [uploadMutation]);

  // Handle paste event for clipboard images
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData.items;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (file) {
          handleFileUpload(file);
        }
        return;
      }
    }
    // Let default paste behavior handle text
  }, [handleFileUpload]);

  // Handle file input change
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      Array.from(files).forEach((file) => handleFileUpload(file));
    }
    // Reset input so the same file can be selected again
    e.target.value = '';
  };

  // Remove pending attachment
  const handleRemoveAttachment = (id: string) => {
    setPendingAttachments((prev) => {
      const att = prev.find((a) => a.id === id);
      if (att?.previewUrl) {
        URL.revokeObjectURL(att.previewUrl);
      }
      return prev.filter((a) => a.id !== id);
    });
  };

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    const cursorPosition = e.target.selectionStart;

    setContent(newContent);
    handleTyping();

    // Check for @ mentions
    detectMention(newContent, cursorPosition);
  };

  const handleMentionSelect = (user: { id: number; full_name: string; first_name: string; last_name: string }) => {
    const displayName = user.full_name || `${user.first_name} ${user.last_name}`.trim();

    // Replace the @query with @username
    const beforeMention = content.slice(0, mentionStartIndex);
    const afterMention = content.slice(mentionStartIndex + mentionQuery.length + 1);
    const newContent = `${beforeMention}@${displayName} ${afterMention}`;

    setContent(newContent);

    // Add to mentioned users if not already there
    if (!mentionedUsers.some((u) => u.id === user.id)) {
      setMentionedUsers((prev) => [...prev, { id: user.id, name: displayName }]);
    }

    setShowMentions(false);
    setMentionStartIndex(-1);

    // Focus back on textarea
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handleRemoveMention = (userId: number) => {
    setMentionedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const handleSend = async () => {
    const trimmedContent = content.trim();
    const hasAttachments = pendingAttachments.length > 0;
    const isAnyUploading = pendingAttachments.some((att) => att.isUploading);

    // Need either content or attachments, and no uploads in progress
    if ((!trimmedContent && !hasAttachments) || isSending || disabled || isAnyUploading) return;

    setIsSending(true);

    try {
      // Convert pending attachments to MessageAttachment format
      const attachments = pendingAttachments
        .filter((att) => att.url) // Only include successfully uploaded attachments
        .map(({ url, name, type, size }) => ({ url, name, type, size }));

      await onSend({
        content: trimmedContent || '', // Allow empty content if attachments exist
        parent_message_id: replyToMessageId || undefined,
        mentioned_user_ids: mentionedUsers.map((u) => String(u.id)),
        attachments: attachments.length > 0 ? attachments : undefined,
      });

      // Clear state
      setContent('');
      setMentionedUsers([]);
      // Clean up preview URLs
      pendingAttachments.forEach((att) => {
        if (att.previewUrl) {
          URL.revokeObjectURL(att.previewUrl);
        }
      });
      setPendingAttachments([]);

      // Focus after React re-renders
      setTimeout(() => {
        textareaRef.current?.focus();
      }, 0);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Don't send if mention autocomplete is open (let it handle the keys)
    if (showMentions) {
      if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        return; // Let MentionAutocomplete handle these
      }
      if (e.key === 'Escape') {
        setShowMentions(false);
        return;
      }
    }

    // Send on Enter (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }

    // Cancel reply on Escape
    if (e.key === 'Escape' && replyToMessageId) {
      onCancelReply?.();
    }
  };

  // Clean up typing timeout
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Handle emoji selection
  const handleEmojiSelect = (emoji: string) => {
    setContent((prev) => prev + emoji);
    setEmojiPickerOpen(false);
    textareaRef.current?.focus();
  };

  // Handle click outside to close emoji picker and mentions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerOpen &&
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node) &&
        emojiButtonRef.current &&
        !emojiButtonRef.current.contains(event.target as Node)
      ) {
        setEmojiPickerOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [emojiPickerOpen]);

  return (
    <Box
      ref={composerRef}
      sx={{
        p: { xs: 1, md: 2 },
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.surface',
      }}
    >
      {/* Mention Autocomplete */}
      {showMentions && (
        <MentionAutocomplete
          searchQuery={mentionQuery}
          onSelect={handleMentionSelect}
          onClose={() => setShowMentions(false)}
          position={mentionPosition}
          currentUserId={currentUserId}
        />
      )}

      {/* Reply indicator */}
      {replyToMessageId && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
            px: 1,
            py: 0.5,
            borderRadius: 'sm',
            bgcolor: 'background.level1',
          }}
        >
          <Box sx={{ fontSize: 'sm', color: 'text.secondary' }}>
            Replying to thread
          </Box>
          <Button
            size="sm"
            variant="plain"
            color="neutral"
            onClick={onCancelReply}
          >
            Cancel
          </Button>
        </Box>
      )}

      {/* Mentioned users chips */}
      {mentionedUsers.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {mentionedUsers.map((user) => (
            <Chip
              key={user.id}
              size="sm"
              variant="soft"
              color="primary"
              endDecorator={
                <Box
                  component="span"
                  sx={{ cursor: 'pointer', ml: 0.5 }}
                  onClick={() => handleRemoveMention(user.id)}
                >
                  ×
                </Box>
              }
            >
              @{user.name}
            </Chip>
          ))}
        </Box>
      )}

      {/* Pending attachments preview */}
      {pendingAttachments.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1,
            p: 1,
            borderRadius: 'sm',
            bgcolor: 'background.level1',
          }}
        >
          {pendingAttachments.map((att) => (
            <Box
              key={att.id}
              sx={{
                position: 'relative',
                width: 64,
                height: 64,
                borderRadius: 'sm',
                overflow: 'hidden',
                bgcolor: 'background.level2',
              }}
            >
              <Box
                component="img"
                src={att.previewUrl}
                alt={att.name}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: att.isUploading ? 0.5 : 1,
                }}
              />
              {att.isUploading && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size="sm" />
                </Box>
              )}
              {!att.isUploading && (
                <IconButton
                  size="sm"
                  variant="solid"
                  color="neutral"
                  onClick={() => handleRemoveAttachment(att.id)}
                  sx={{
                    position: 'absolute',
                    top: 2,
                    right: 2,
                    minWidth: 20,
                    minHeight: 20,
                    p: 0,
                  }}
                >
                  <CloseIcon size={14} />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />

      {/* Composer */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
        {/* Attachment button */}
        <Tooltip title="Attach image">
          <IconButton
            variant="plain"
            color="neutral"
            disabled={disabled}
            onClick={() => fileInputRef.current?.click()}
          >
            <AttachFileIcon />
          </IconButton>
        </Tooltip>

        {/* Text input */}
        <Textarea
          slotProps={{
            textarea: {
              ref: textareaRef,
            },
          }}
          placeholder={placeholder}
          value={content}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          disabled={disabled || isSending}
          minRows={1}
          maxRows={6}
          sx={{
            flex: 1,
            '--Textarea-focusedHighlight': 'var(--joy-palette-primary-500)',
          }}
        />

        {/* Emoji button */}
        <Box sx={{ position: 'relative' }}>
          <Tooltip title="Add emoji">
            <IconButton
              ref={emojiButtonRef}
              variant="plain"
              color="neutral"
              disabled={disabled}
              onClick={() => setEmojiPickerOpen((prev) => !prev)}
            >
              <EmojiEmotionsIcon />
            </IconButton>
          </Tooltip>

          {/* Emoji picker popover */}
          {emojiPickerOpen && (
            <Box
              ref={emojiPickerRef}
              sx={{
                position: 'absolute',
                bottom: '100%',
                right: 0,
                mb: 1,
                zIndex: 1300,
              }}
            >
              <EmojiPicker onEmojiSelect={handleEmojiSelect} />
            </Box>
          )}
        </Box>

        {/* Send button */}
        <IconButton
          variant="solid"
          color="primary"
          disabled={
            (!content.trim() && pendingAttachments.length === 0) ||
            disabled ||
            isSending ||
            pendingAttachments.some((att) => att.isUploading)
          }
          onClick={handleSend}
        >
          <SendIcon />
        </IconButton>
      </Box>

      {/* Hint text */}
      <Box sx={{ mt: 0.5, px: 1 }}>
        <Box
          component="span"
          sx={{ fontSize: 'xs', color: 'text.tertiary' }}
        >
          Press <kbd style={{ fontFamily: 'monospace' }}>Enter</kbd> to send,{' '}
          <kbd style={{ fontFamily: 'monospace' }}>@</kbd> to mention
        </Box>
      </Box>
    </Box>
  );
};

export default MessageComposer;
